import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';
import { useMemo, useState } from 'react';
import { countries, flag } from '../../lib/countries';
import { t as tr } from '../../i18n';

/** Country code picker + national number. Reports E.164 (or null when empty, '' when invalid). */
export function PhoneInput({ value, onChange }: { value: string | null; onChange: (e164: string | null | '') => void }) {
  const initial = useMemo(() => (value ? parsePhoneNumberFromString(value) : undefined), [value]);
  const [country, setCountry] = useState<CountryCode>(initial?.country ?? 'VN');
  const [number, setNumber] = useState(initial?.formatNational() ?? '');
  const list = countries();
  const dial = list.find((c) => c.code === country)?.dial ?? '';

  const report = (c: CountryCode, n: string) => {
    if (!n.trim()) return onChange(null);
    const p = parsePhoneNumberFromString(n, c);
    onChange(p?.isValid() ? p.number : '');
  };

  return (
    <div className="flex h-12 items-center rounded-xl border border-ink-700 bg-ink-850 focus-within:border-volt">
      <label className="relative flex h-full shrink-0 items-center gap-1 border-r border-ink-700 pl-3 pr-2 text-sm">
        <span aria-hidden="true">{flag(country)}</span>
        <span>{dial}</span>
        <span className="text-ink-500">▾</span>
        <select
          aria-label={tr('Country code')}
          value={country}
          onChange={(e) => {
            const c = e.target.value as CountryCode;
            setCountry(c);
            report(c, number);
          }}
          className="absolute inset-0 opacity-0"
        >
          {list.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name} ({c.dial})
            </option>
          ))}
        </select>
      </label>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={number}
        onChange={(e) => {
          setNumber(e.target.value);
          report(country, e.target.value);
        }}
        className="h-full min-w-0 flex-1 bg-transparent px-3 outline-none"
        data-testid="phone-input"
        aria-label={tr('Phone')}
      />
    </div>
  );
}
